import { useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Plus,
  Clock,
  Users,
  Search,
  RotateCcw,
  UserCircle,
  MoreVertical,
  Pencil,
  Trash2,
  Pause,
  UserPlus,
} from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { lcTimeslotApi, lcTimeslotClosureApi, Timeslot, TimeslotCreateRequest, TimeslotUpdateRequest } from '@/api/lc'
import { memberApi, Member } from '@/api/b2b'
import { AdminSearchInput } from '@/components/admin/common/AdminSearchInput'
import { ReEnrollmentModal } from '@/components/admin/members/ReEnrollmentModal'
import ClosureModal from './components/ClosureModal'
import AssignmentModal from './components/AssignmentModal'
import ClassRosterPanel from './components/ClassRosterPanel'

const DAYS_OF_WEEK = [
  { key: 'mon' },
  { key: 'tue' },
  { key: 'wed' },
  { key: 'thu' },
  { key: 'fri' },
  { key: 'sat' },
  { key: 'sun' },
] as const

type DayKey = typeof DAYS_OF_WEEK[number]['key']

// 수업시간 드롭다운 (T3): 30~120분 10분 단위.
const DURATION_OPTIONS = [30, 40, 50, 60, 70, 80, 90, 100, 110, 120] as const
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'))
const MINUTE_OPTIONS = ['00', '10', '20', '30', '40', '50'] as const

interface DaySchedule {
  startTime: string
  durationMinutes: number
}

type ScheduleData = Partial<Record<DayKey, DaySchedule>>

interface ScheduleEntry extends DaySchedule {
  dayKey: DayKey
}

interface ScheduleConflict {
  dayKey: DayKey
  startTime: string
  timeslotName: string
}

interface Props {
  trialEnabled?: boolean
  headerActions?: ReactNode
  canReEnroll?: boolean
}

export default function TimeslotManagement({ trialEnabled = false, headerActions, canReEnroll = false }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTimeslot, setEditingTimeslot] = useState<Timeslot | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  // 휴강 관리 모달 대상 클래스 (P2)
  const [closureTimeslot, setClosureTimeslot] = useState<Timeslot | null>(null)
  // 원생 배정 모달 대상 클래스 (P3)
  const [assignTimeslot, setAssignTimeslot] = useState<Timeslot | null>(null)
  const [reEnrollTimeslot, setReEnrollTimeslot] = useState<Timeslot | null>(null)
  // 원생 목록(roster) 인라인 표시 대상 클래스 (P4)
  const [rosterTimeslot, setRosterTimeslot] = useState<Timeslot | null>(null)

  // Form state
  const [formName, setFormName] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formTeacherUserId, setFormTeacherUserId] = useState<number | undefined>()
  const [formSchedule, setFormSchedule] = useState<ScheduleData>({})

  const { data: timeslotsData, isLoading } = useQuery({
    queryKey: ['lcTimeslots'],
    // 삭제 대체로 비활성된 클래스(체험 이력 보유)는 사용자에게 삭제된 것으로 보여야 하므로 활성만 조회한다.
    queryFn: () => lcTimeslotApi.getAll(),
  })

  const { data: membersData } = useQuery({
    queryKey: ['allMembers'],
    queryFn: () => memberApi.getAll(),
  })

  // 휴강 상태 배지(축4): org 전체 휴강 1회 조회 후 timeslotId→오늘 ONGOING 여부 매핑 (N+1 회피).
  const { data: closuresData } = useQuery({
    queryKey: ['lcTimeslotClosures'],
    queryFn: () => lcTimeslotClosureApi.list(),
  })
  const ongoingTimeslotIds = new Set(
    (closuresData?.data?.data || []).filter(c => c.state === 'ONGOING').map(c => c.timeslotId)
  )

  const timeslots = timeslotsData?.data?.data || []
  const members = membersData?.data?.data || []
  // 강사 후보 (T5, 엣지5): owner+admin 중 활성만. 학습자(member)는 제외.
  const instructorCandidates = members.filter(
    m => (m.role === 'owner' || m.role === 'admin') && m.status === 'active'
  )

  // 강사 구분 라벨 (T5): owner=원장, admin=lcAdminRoleName(실장/교사 등) 또는 '직원' 폴백.
  const instructorBadge = (m: Member): string =>
    m.role === 'owner' ? t('timeslot.role.owner') : (m.lcAdminRoleName || t('timeslot.role.staff'))
  const instructorLabel = (m: Member): string => `${instructorBadge(m)} · ${m.fullName} · ${m.email}`

  const filteredTimeslots = timeslots.filter(ts =>
    ts.name.toLowerCase().includes(appliedSearch.toLowerCase()) ||
    (ts.description || '').toLowerCase().includes(appliedSearch.toLowerCase())
  )

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const query = searchTerm.trim()
    if (!query) return
    setSearchTerm(query)
    setAppliedSearch(query)
  }

  const resetSearch = () => {
    setSearchTerm('')
    setAppliedSearch('')
  }

  // 주간 수업 횟수 (T4, 엣지9): 선택된 요일 수.
  const weeklyCount = (schedule: ScheduleData): number =>
    DAYS_OF_WEEK.filter(d => schedule[d.key]).length

  const getApiErrorCode = (err: unknown): string | undefined =>
    (err as { response?: { data?: { error?: { code?: string } } } })
      .response?.data?.error?.code

  const createMutation = useMutation({
    mutationFn: ({ data }: { data: TimeslotCreateRequest; conflict: ScheduleConflict | null }) =>
      lcTimeslotApi.create(data),
    onSuccess: (_response, { conflict }) => {
      queryClient.invalidateQueries({ queryKey: ['lcTimeslots'] })
      setShowCreateModal(false)
      resetForm()
      if (conflict) {
        toast.warning(t('timeslot.toast.createSuccessWithScheduleConflict', {
          day: t(`timeslot.days.${conflict.dayKey}`),
          startTime: conflict.startTime,
          name: conflict.timeslotName,
        }))
      } else {
        toast.success(t('timeslot.toast.createSuccess'))
      }
    },
    onError: () => {
      toast.error(t('timeslot.toast.createFailed'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: {
      id: number
      data: TimeslotUpdateRequest
      conflict: ScheduleConflict | null
    }) =>
      lcTimeslotApi.update(id, data),
    onSuccess: (_response, { conflict }) => {
      queryClient.invalidateQueries({ queryKey: ['lcTimeslots'] })
      setEditingTimeslot(null)
      resetForm()
      if (conflict) {
        toast.warning(t('timeslot.toast.updateSuccessWithScheduleConflict', {
          day: t(`timeslot.days.${conflict.dayKey}`),
          startTime: conflict.startTime,
          name: conflict.timeslotName,
        }))
      } else {
        toast.success(t('timeslot.toast.updateSuccess'))
      }
    },
    onError: () => {
      toast.error(t('timeslot.toast.updateFailed'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => lcTimeslotApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lcTimeslots'] })
      // 무료체험 이력이 남은 클래스는 백엔드가 비활성 처리(outcome=DEACTIVATED)하지만,
      // 목록이 활성만 보여주므로 사용자에게는 삭제와 동일하게 안내한다.
      toast.success(t('timeslot.toast.deleteSuccess'))
    },
    onError: (err: unknown) => {
      // T6: 배정 원생으로 삭제가 막히면 안내 토스트.
      const code = getApiErrorCode(err)
      if (code === 'TIMESLOT_HAS_ASSIGNED_STUDENTS') {
        toast.error(t('timeslot.toast.deleteBlocked'))
      } else {
        toast.error(t('timeslot.toast.deleteFailed'))
      }
    },
  })

  const resetForm = () => {
    setFormName('')
    setFormDescription('')
    setFormTeacherUserId(undefined)
    setFormSchedule({})
  }

  const parseSchedule = (scheduleStr: string): ScheduleData => {
    try {
      const parsed = JSON.parse(scheduleStr)
      return typeof parsed === 'object' && parsed !== null ? parsed : {}
    } catch {
      return {}
    }
  }

  const findScheduleConflict = (schedule: ScheduleData): ScheduleConflict | null => {
    if (formTeacherUserId == null) return null

    for (const { key } of DAYS_OF_WEEK) {
      const current = schedule[key]
      if (!current?.startTime) continue

      for (const timeslot of timeslots) {
        if (!timeslot.isActive || timeslot.timeslotId === editingTimeslot?.timeslotId) continue
        if (timeslot.teacherUserId !== formTeacherUserId) continue

        const existing = parseSchedule(timeslot.schedule)[key]
        if (existing?.startTime === current.startTime) {
          return {
            dayKey: key,
            startTime: current.startTime,
            timeslotName: timeslot.name,
          }
        }
      }
    }

    return null
  }

  const openEditModal = (timeslot: Timeslot) => {
    setEditingTimeslot(timeslot)
    setFormName(timeslot.name)
    setFormDescription(timeslot.description || '')
    setFormTeacherUserId(timeslot.teacherUserId ?? undefined)
    setFormSchedule(parseSchedule(timeslot.schedule))
  }

  const handleDelete = (timeslot: Timeslot) => {
    setOpenMenuId(null)
    if (confirm(t('timeslot.confirm.delete'))) {
      deleteMutation.mutate(timeslot.timeslotId)
    }
  }

  const handleSubmit = () => {
    if (!formName.trim()) {
      toast.error(t('timeslot.toast.nameRequired'))
      return
    }

    const conflict = findScheduleConflict(formSchedule)
    const scheduleJson = JSON.stringify(formSchedule)

    if (editingTimeslot) {
      updateMutation.mutate({
        id: editingTimeslot.timeslotId,
        conflict,
        data: {
          name: formName,
          description: formDescription || undefined,
          teacherUserId: formTeacherUserId,
          schedule: scheduleJson,
        },
      })
    } else {
      createMutation.mutate({
        conflict,
        data: {
          name: formName,
          description: formDescription || undefined,
          teacherUserId: formTeacherUserId,
          schedule: scheduleJson,
        },
      })
    }
  }

  // 수업시간 select 옵션 (T3, 엣지10): 범위 밖 기존값(예 180)은 맨 위에 임시 보존.
  const durationOptionsFor = (current: number): number[] => {
    if (DURATION_OPTIONS.includes(current as typeof DURATION_OPTIONS[number])) {
      return [...DURATION_OPTIONS]
    }
    return [current, ...DURATION_OPTIONS]
  }

  const getScheduleEntries = (schedule: ScheduleData): ScheduleEntry[] =>
    DAYS_OF_WEEK.flatMap(({ key }) => {
      const daySchedule = schedule[key]
      return daySchedule ? [{ dayKey: key, ...daySchedule }] : []
    })

  const parseStartTime = (startTime: string): { hour: string; minute: string } => {
    const [hour = '00', minute = '00'] = startTime.split(':')
    return {
      hour: hour.padStart(2, '0'),
      minute: MINUTE_OPTIONS.includes(minute as typeof MINUTE_OPTIONS[number]) ? minute : '00',
    }
  }

  const updateStartTime = (key: DayKey, daySchedule: DaySchedule, hour: string, minute: string) => {
    const newSchedule = { ...formSchedule }
    newSchedule[key] = { ...daySchedule, startTime: `${hour}:${minute}` }
    setFormSchedule(newSchedule)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('label.loading', { ns: 'common' })}</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <form className="min-w-0 flex-1" onSubmit={submitSearch}>
          <FieldGroup className="gap-2">
            <Field orientation="responsive">
              <FieldLabel htmlFor="timeslot-search" className="sr-only">
                {t('timeslot.search.label')}
              </FieldLabel>
              <AdminSearchInput
                id="timeslot-search"
                placeholder={t('timeslot.search.placeholder')}
                value={searchTerm}
                maxLength={100}
                onValueChange={setSearchTerm}
                onClear={() => setSearchTerm('')}
                clearLabel={t('timeslot.search.clear')}
                containerClassName="@md/field-group:max-w-md"
              />
              <div className="flex gap-2">
                <Button type="submit" disabled={!searchTerm.trim() || searchTerm.trim() === appliedSearch}>
                  <Search data-icon="inline-start" />
                  {t('timeslot.search.submit')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!searchTerm && !appliedSearch}
                  onClick={resetSearch}
                >
                  <RotateCcw data-icon="inline-start" />
                  {t('timeslot.search.reset')}
                </Button>
              </div>
            </Field>
          </FieldGroup>
        </form>
        <div className="flex shrink-0 flex-wrap items-center gap-2 lg:ml-auto">
          {headerActions}
          <button
            type="button"
            onClick={() => {
              resetForm()
              setShowCreateModal(true)
            }}
            className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            {t('timeslot.actions.addTimeslot')}
          </button>
        </div>
      </div>

      {/* Timeslot List */}
      {filteredTimeslots.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Clock className="w-16 h-16 text-gray-300 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">{t('timeslot.empty.title')}</h3>
          <p className="mt-2 text-gray-500">{t('timeslot.empty.subtitle')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTimeslots.map((timeslot) => {
            const scheduleEntries = getScheduleEntries(parseSchedule(timeslot.schedule))
            const weekly = scheduleEntries.length
            const isRosterSelected = rosterTimeslot?.timeslotId === timeslot.timeslotId
            return (
              <div
                key={timeslot.timeslotId}
                className={`relative rounded-xl border p-5 shadow-sm transition-shadow ${
                  timeslot.isActive ? 'bg-white' : 'bg-red-50'
                } ${
                  isRosterSelected
                    ? 'border-primary-400 ring-2 ring-primary-100 shadow-md'
                    : timeslot.isActive ? 'border-gray-200' : 'border-red-200'
                }`}
              >
                <button
                  type="button"
                  aria-label={t('timeslot.actions.viewRoster', { name: timeslot.name })}
                  aria-pressed={isRosterSelected}
                  onClick={() => setRosterTimeslot(timeslot)}
                  className="absolute inset-0 z-10 cursor-pointer rounded-xl transition-colors hover:bg-primary-50/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                />
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{timeslot.name}</h3>
                    {/* T9: 상태(운영) + 주간 횟수 배지 */}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {timeslot.isActive ? (
                        ongoingTimeslotIds.has(timeslot.timeslotId) ? (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                            {t('timeslot.status.suspended')}
                          </span>
                        ) : (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                            {t('timeslot.status.operating')}
                          </span>
                        )
                      ) : (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                          {t('status.inactive', { ns: 'common' })}
                        </span>
                      )}
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {t('timeslot.weeklyCount', { n: weekly })}
                      </span>
                    </div>
                  </div>
                  {/* T7: [관리▼] 드롭다운 */}
                  <div className="relative z-20">
                    <button
                      type="button"
                      onClick={() => setOpenMenuId(openMenuId === timeslot.timeslotId ? null : timeslot.timeslotId)}
                      className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      {t('timeslot.actions.manage')}
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {openMenuId === timeslot.timeslotId && (
                      <div
                        role="menu"
                        className="absolute right-0 z-10 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                      >
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setOpenMenuId(null); openEditModal(timeslot) }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          {t('timeslot.actions.edit')}
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => handleDelete(timeslot)}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {t('timeslot.actions.delete')}
                        </button>
                        {/* 원생 배정 (P3): 배정가능 원생 표 + 학습과정 선택 모달 오픈 */}
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setOpenMenuId(null); setAssignTimeslot(timeslot) }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          {t('timeslot.actions.assign')}
                        </button>
                        {canReEnroll ? (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => { setOpenMenuId(null); setReEnrollTimeslot(timeslot) }}
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <UserPlus className="w-3.5 h-3.5" />
                            {t('timeslot.actions.reEnroll')}
                          </button>
                        ) : null}
                        {/* 휴강 관리 (P2): 클래스 휴강 기간 등록/관리 모달 오픈 */}
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setOpenMenuId(null); setClosureTimeslot(timeslot) }}
                          title={t('timeslot.actions.suspendTooltip')}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Pause className="w-3.5 h-3.5" />
                          {t('timeslot.actions.suspend')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {timeslot.description && (
                  <p className="text-sm text-gray-500 mb-3">{timeslot.description}</p>
                )}

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>
                      {t('timeslot.card.studentCount', {
                        current: timeslot.currentStudents,
                        onLeave: timeslot.onLeaveStudents,
                      })}
                    </span>
                  </div>
                  {timeslot.teacherName && (
                    <div className="flex items-center gap-2">
                      <UserCircle className="w-4 h-4 text-gray-400" />
                      <span>{t('timeslot.card.teacher', { name: timeslot.teacherName })}</span>
                    </div>
                  )}
                  {scheduleEntries.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <div
                        role="list"
                        aria-label={t('timeslot.form.schedule')}
                        className="flex min-w-0 flex-wrap gap-1.5"
                      >
                        {scheduleEntries.map(({ dayKey, startTime, durationMinutes }) => (
                          <span
                            key={dayKey}
                            role="listitem"
                            aria-label={t('timeslot.scheduleSummary.entry', {
                              day: t(`timeslot.days.${dayKey}`),
                              startTime,
                              minutes: durationMinutes,
                            })}
                            className="inline-flex items-center overflow-hidden rounded-md border border-primary-100 bg-primary-50 text-xs font-medium shadow-[0_1px_1px_rgba(15,23,42,0.03)]"
                          >
                            <span className="bg-primary-100 px-1.5 py-1 font-bold text-primary-700">
                              {t(`timeslot.days.${dayKey}`)}
                            </span>
                            <span className="px-1.5 py-1 tabular-nums text-gray-700">
                              {startTime}
                            </span>
                            <span className="border-l border-primary-100 px-1.5 py-1 text-gray-500">
                              {t('timeslot.duration.option', { minutes: durationMinutes })}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

              </div>
            )
          })}
        </div>
      )}

      {rosterTimeslot && (
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <ClassRosterPanel
            timeslot={rosterTimeslot}
            maxHeightClassName="max-h-[36rem]"
            trialEnabled={trialEnabled}
          />
        </section>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || !!editingTimeslot} onOpenChange={(open) => { if (!open) { setShowCreateModal(false); setEditingTimeslot(null); resetForm() } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTimeslot ? t('timeslot.modal.editTitle') : t('timeslot.modal.createTitle')}</DialogTitle>
          </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('timeslot.form.name')} *</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder={t('timeslot.form.namePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('label.description', { ns: 'common' })}</label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder={t('timeslot.form.descriptionPlaceholder')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            {/* 담당강사 후보 (T5): owner+admin, 구분·이름·이메일 표기. */}
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('timeslot.form.teacher')}</label>
            <select
              value={formTeacherUserId || ''}
              onChange={(e) => setFormTeacherUserId(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">{t('timeslot.form.teacherNone')}</option>
              {/* 엣지5: 기존 지정 강사가 후보에 없으면(비활성 등) 응답 teacherName으로 옵션 유지 */}
              {editingTimeslot?.teacherUserId != null &&
                !instructorCandidates.some(m => m.userId === editingTimeslot.teacherUserId) && (
                  <option value={editingTimeslot.teacherUserId}>
                    {editingTimeslot.teacherName || `#${editingTimeslot.teacherUserId}`}
                  </option>
                )}
              {instructorCandidates.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {instructorLabel(m)}
                </option>
              ))}
            </select>
          </div>

          {/* 주간 수업 횟수 (T4): 폼에서 라이브 표시 */}
          <p className="text-sm text-gray-600">{t('timeslot.weeklyCount', { n: weeklyCount(formSchedule) })}</p>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('timeslot.form.schedule')}</label>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
              {DAYS_OF_WEEK.map(({ key }) => {
                const daySchedule = formSchedule[key]
                const isEnabled = !!daySchedule
                const dayLabel = t(`timeslot.days.${key}`)
                const startTime = daySchedule ? parseStartTime(daySchedule.startTime) : null
                return (
                  <div key={key} className="flex items-center gap-3 px-3 py-2">
                    <label className="flex items-center gap-2 w-10 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) => {
                          const newSchedule = { ...formSchedule }
                          if (e.target.checked) {
                            newSchedule[key] = { startTime: '16:00', durationMinutes: 60 }
                          } else {
                            delete newSchedule[key]
                          }
                          setFormSchedule(newSchedule)
                        }}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">{dayLabel}</span>
                    </label>
                    {isEnabled && startTime && (
                      <>
                        <select
                          aria-label={`${dayLabel} ${t('timeslot.form.hour')}`}
                          value={startTime.hour}
                          onChange={(e) => {
                            updateStartTime(key, daySchedule, e.target.value, startTime.minute)
                          }}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                        >
                          {HOUR_OPTIONS.map((hour) => (
                            <option key={hour} value={hour}>{hour}</option>
                          ))}
                        </select>
                        <select
                          aria-label={`${dayLabel} ${t('timeslot.form.minute')}`}
                          value={startTime.minute}
                          onChange={(e) => {
                            updateStartTime(key, daySchedule, startTime.hour, e.target.value)
                          }}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                        >
                          {MINUTE_OPTIONS.map((minute) => (
                            <option key={minute} value={minute}>{minute}</option>
                          ))}
                        </select>
                        <select
                          value={daySchedule.durationMinutes}
                          onChange={(e) => {
                            const newSchedule = { ...formSchedule }
                            newSchedule[key] = { ...daySchedule, durationMinutes: parseInt(e.target.value) }
                            setFormSchedule(newSchedule)
                          }}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                        >
                          {durationOptionsFor(daySchedule.durationMinutes).map((m) => (
                            <option key={m} value={m}>{t('timeslot.duration.option', { minutes: m })}</option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
            <p className="mt-1 text-xs text-gray-400">{t('timeslot.form.scheduleHelp')}</p>
          </div>

        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => {
              setShowCreateModal(false)
              setEditingTimeslot(null)
              resetForm()
            }}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {t('button.cancel', { ns: 'common' })}
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {editingTimeslot ? t('button.edit', { ns: 'common' }) : t('button.create', { ns: 'common' })}
          </button>
        </div>
        </DialogContent>
      </Dialog>

      {/* 휴강 관리 모달 (P2) */}
      {closureTimeslot && (
        <ClosureModal timeslot={closureTimeslot} onClose={() => setClosureTimeslot(null)} />
      )}

      {/* 원생 배정 모달 (P3) */}
      {assignTimeslot && (
        <AssignmentModal
          timeslot={assignTimeslot}
          trialEnabled={trialEnabled}
          onClose={() => setAssignTimeslot(null)}
        />
      )}
      {canReEnroll && reEnrollTimeslot && (
        <ReEnrollmentModal
          initialTimeslot={reEnrollTimeslot}
          onClose={() => setReEnrollTimeslot(null)}
        />
      )}
    </div>
  )
}
